import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import redRockImage from '../assets/images/Image 2_Red Rock.jpg';
import { motion, AnimatePresence } from 'framer-motion';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { FeatureAccessGate } from '../components/FeatureAccessGate';
import SEO from '../components/SEO';

// Add custom styles for enhanced interactions
const styles = `
  .feature-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    transform-style: preserve-3d;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.5s forwards;
    border: 1px solid rgba(59, 130, 246, 0.1);
  }

  .feature-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.3);
    border-color: rgba(59, 130, 246, 0.2);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.9) 100%);
  }

  .feature-icon {
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
    border-radius: 12px;
    margin-bottom: 1rem;
  }

  .feature-card:hover .feature-icon {
    transform: scale(1.1) rotate(-5deg);
    background: linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%);
  }

  .case-card {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%);
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.5s forwards;
    border: 1px solid rgba(59, 130, 246, 0.1);
    position: relative;
    overflow: hidden;
  }

  .case-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, #60A5FA, #93C5FD);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .case-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 20px 30px -15px rgba(66, 153, 225, 0.25);
    border-color: rgba(59, 130, 246, 0.2);
  }

  .case-card:hover::before {
    opacity: 1;
  }

  .section-title {
    background: linear-gradient(135deg, 
      #000 0%,
      #333 25%,
      #000 50%,
      #666 75%,
      #000 100%
    );
    -webkit-background-clip: text;
    color: transparent;
    position: relative;
    display: inline-block;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.1);
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(to right,
      transparent 0%,
      rgba(0, 0, 0, 0.3) 25%,
      rgba(0, 0, 0, 0.5) 50%,
      rgba(0, 0, 0, 0.3) 75%,
      transparent 100%
    );
    border-radius: 2px;
  }

  .section-title::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 100%
    );
  }

  .preview-button {
    position: relative;
    overflow: hidden;
  }

  .preview-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, #60A5FA, #93C5FD);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  .preview-button:hover::before {
    opacity: 0.1;
  }

  /* Tooltip styles to ensure they appear on top */
  [title] {
    position: relative;
  }

  [title]:hover::after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    white-space: nowrap;
    z-index: 9999;
    pointer-events: none;
    margin-bottom: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  [title]:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
    z-index: 9999;
    pointer-events: none;
    margin-bottom: 2px;
  }

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .hero-content {
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.6s forwards;
  }

  .hero-image {
    opacity: 0;
    transform: translateX(20px);
    animation: fadeInRight 0.8s 0.2s forwards;
  }

  @keyframes fadeInRight {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .modal-backdrop {
    opacity: 0;
    animation: fadeIn 0.3s forwards;
  }

  .modal-content {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
    animation: modalIn 0.3s forwards;
  }

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }

  @keyframes modalIn {
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

const REDROCK_PROOF_CAROUSEL_MS = 6000;
const REDROCK_PROOF_SLIDE_MS = 500;
const REDROCK_PROOF_SLIDES = [
  { src: '/images/landing/redrock-proof-study.png', alt: 'Red Rock Study Information — Start Simulation', title: 'Study Information', subtitle: 'Start Simulation · Brief · Evidence and journal' },
  { src: '/images/landing/redrock-proof-analysis.png', alt: 'Red Rock Analysis — Stage Recap and coach breakdown', title: 'Analysis and Stage Recap', subtitle: 'Section scores · Win / Fix / Rule · Next action' },
  { src: '/images/landing/redrock-coach-feedback.png', alt: 'Red Rock Run review — overall score and next-case recommendation', title: 'Run review', subtitle: 'Section scores · Evidence review · Report and graph · Next-case recommendation' },
];

function RedRockProofBlock({ navigate }) {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [noTransition, setNoTransition] = useState(false);
  const prevIndexRef = useRef(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % REDROCK_PROOF_SLIDES.length);
    }, REDROCK_PROOF_CAROUSEL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (carouselIndex === 0 && prevIndexRef.current === REDROCK_PROOF_SLIDES.length - 1) {
      setNoTransition(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setNoTransition(false));
      });
      return () => cancelAnimationFrame(id);
    }
    prevIndexRef.current = carouselIndex;
  }, [carouselIndex]);

  const currentSlide = REDROCK_PROOF_SLIDES[carouselIndex];

  return (
    <section id="redrock-simulation" className="py-12 md:py-20 bg-white px-4 sm:px-6 md:px-10 lg:px-16 relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
      </div>
      <div className="max-w-[120rem] mx-auto relative">
        <div className="w-full flex justify-center mb-6 md:mb-8">
          <h2 className="text-3xl md:text-4xl font-bold section-title tracking-tight">See how our Red Rock simulator works</h2>
        </div>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-10 md:mb-12 text-sm md:text-base">
          Try the real Red Rock simulation: timed runs, evidence and report phases, and clear feedback so you know where you stood and what to fix next. No vague coaching—section scores, journal alignment, and a concrete next step every time.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.9fr] gap-8 lg:gap-10 items-center">
          <div className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-xl overflow-hidden">
            <div className="relative w-full aspect-[4/3] min-h-[300px] sm:min-h-[400px] lg:min-h-[480px] bg-slate-50 overflow-hidden">
              <div
                className="flex absolute inset-0"
                style={{
                  width: `${REDROCK_PROOF_SLIDES.length * 100}%`,
                  transform: `translateX(-${carouselIndex * (100 / REDROCK_PROOF_SLIDES.length)}%)`,
                  transition: noTransition ? 'none' : `transform ${REDROCK_PROOF_SLIDE_MS}ms cubic-bezier(0.32,0.72,0,1)`,
                }}
              >
                {REDROCK_PROOF_SLIDES.map((slide, i) => (
                  <div
                    key={i}
                    className="flex h-full flex-shrink-0 items-center justify-center p-0.5"
                    style={{ width: `${100 / REDROCK_PROOF_SLIDES.length}%` }}
                  >
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      className="max-w-full max-h-full object-contain object-center"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="px-4 py-2.5 border-t border-slate-100 bg-white">
              <div className="text-sm font-semibold text-gray-900">{currentSlide.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{currentSlide.subtitle}</div>
            </div>
            <div className="flex justify-center gap-2 py-2 bg-slate-50/50">
              {REDROCK_PROOF_SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCarouselIndex(i)}
                  className={`h-2 rounded-full transition-all ${i === carouselIndex ? 'w-6 bg-sky-600' : 'w-2 bg-slate-300 hover:bg-slate-400'}`}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Evidence', text: 'See whether you collected the right signals and used the journal well.' },
              { label: 'Analysis', text: 'See whether your answers and reasoning supported the recommendation.' },
              { label: 'Report and graph', text: 'See whether your report blanks and chart choices were on target.' },
              { label: 'Next move', text: 'Know exactly what to change on the next rep.' },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-3 rounded-xl border border-slate-200/80 bg-white p-5 text-left"
              >
                <span className="text-blue-600 font-semibold text-base shrink-0">{item.label}:</span>
                <span className="text-gray-600 text-base">{item.text}</span>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.24 }}
              className="pt-2"
            >
              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/redrock')}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition w-full sm:w-auto min-w-[180px]"
              >
                Try the Red Rock Simulation
                <span className="opacity-90">→</span>
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function RedRockGuidePage() {
  const [selectedCase, setSelectedCase] = useState(null);
  const [showAdditionalCases, setShowAdditionalCases] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [activeVideoTab, setActiveVideoTab] = useState('tutorial1');
  const navigate = useNavigate();
  const { canAccess } = useFeatureAccess();

  const videoTabs = [
    {
      id: 'tutorial1',
      title: 'Red Rock Tutorial 1',
      videoId: 'YOUR_VIDEO_ID_1',
      startTime: 0
    },
    {
      id: 'tutorial2',
      title: 'Red Rock Tutorial 2',
      videoId: 'YOUR_VIDEO_ID_2',
      startTime: 0
    }
  ];

  // Scroll to section if hash is present
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash === '#explore') {
        const el = document.getElementById('explore');
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 200);
        }
      }
    };

    // Handle initial load
    handleHashNavigation();

    // Handle hash changes
    const handleHashChange = () => {
      handleHashNavigation();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleNavigation = (path, caseNumber) => {
    if (caseNumber === '1') {
      navigate(path);
      window.scrollTo(0, 0);
      return;
    }
    const featureKey = `case${caseNumber}`;
    if (canAccess(featureKey)) {
      setTimeout(() => {
        navigate(path);
        window.scrollTo(0, 0);
      }, 500);
    }
  };

  const getCaseFeatureKey = (caseNumber) => {
    return `case${caseNumber}`;
  };

  const handleCaseClick = (caseItem) => {
    if (caseItem.isUpgradeCard) {
      setShowAdditionalCases(!showAdditionalCases);
      return;
    }

    // Extract case number from the case string (e.g., "Case 1" -> "1")
    const caseNumber = caseItem.case.split(' ')[1];
    const featureKey = getCaseFeatureKey(caseNumber);

    if (canAccess(featureKey)) {
      setSelectedCase(caseItem);
    }
  };

  const cases = [
    {
      case: 'Case 1',
      level: 'Beginner',
      topic: 'Stratosat – Satellite Launch Planning',
      description: 'Help Stratosat select two satellite technologies to launch within a $40M budget, maximizing global impact, efficiency, and feasibility while balancing costs, strategic ratings, and regional benefits.',
      previewCharts: 3,
      icon: '🛰️',
      path: '/case1',
      isFree: true
    },
    {
      case: 'Case 17',
      level: 'Advanced',
      topic: 'QuantumFlow – Quantum Computing Startup',
      description: 'Analyze quantum platform performance across multiple dimensions including coherence time, gate fidelity, error rates, and algorithm success probabilities to determine optimal investment strategy.',
      previewCharts: 4,
      icon: '⚛️',
      path: '/case17',
      isFree: false
    },
    {
      case: 'Case 10',
      level: 'Advanced',
      topic: 'SkyPortX – Airport Congestion Optimization',
      description: 'Determine which areas of SkyPortX experienced the greatest congestion relief, assess which interventions delivered results, and recommend a strategy to implement scalable changes across all terminals.',
      previewCharts: 3,
      icon: '✈️',
      path: '/case10',
      isFree: false
    },
    {
      case: 'More practice cases',
      level: 'Practice Library',
      topic: 'Rest of practice library',
      description: 'Sign in with a free account to see all 17 practice cases. No payment required. When you\'re ready, try the real Red Rock simulator.',
      icon: '🔓',
      isUpgradeCard: true
    }
  ];

  const additionalCases = [
    {
      case: "Case 2",
      level: "Intermediate",
      topic: "FrostSpan – Glacier Corridor Strategy",
      description: "Determine which glacier zones to monitor, select infrastructure projects to fund, and prioritize resources based on water retention, community access, and melt forecasts.",
      previewCharts: 3,
      icon: "🧊",
      path: "/case2"
    },
    {
      case: "Case 3",
      level: "Advanced",
      topic: "HydraGrid – Smart Energy Rebalance Strategy",
      description: "Optimize renewable energy hub deployment across six regions, balancing demand, degradation rates, and regional risk factors while selecting areas for deprioritization and grid buffering.",
      previewCharts: 3,
      icon: "🔌",
      path: "/case3"
    },
    {
      case: "Case 4",
      level: "Intermediate",
      topic: "SolarFlux City Initiative",
      description: "Analyze sustainability initiatives in a smart city, evaluate emission reduction efficiency, and recommend program optimizations for solar, EV, and energy pricing strategies.",
      previewCharts: 3,
      icon: "🌞",
      path: "/case4"
    },
    {
      case: "Case 5",
      level: "Beginner",
      topic: "Cafe Bloom - Morning Rush Makeover",
      description: "Evaluate operational changes at a busy coffee shop, analyze congestion patterns, and recommend improvements for digital ordering and staffing.",
      previewCharts: 3,
      icon: "☕",
      path: "/case5"
    },
    {
      case: "Case 6",
      level: "Intermediate",
      topic: "BikeBuddies – City Cycling Tracker",
      description: "Analyze cycling patterns and user data to help the city council decide on bike lane expansions based on usage trends and regional activity.",
      previewCharts: 3,
      icon: "🚲",
      path: "/case6"
    },
    {
      case: "Case 7",
      level: "Beginner",
      topic: "FreshFare Market – Weekly Produce Trends",
      description: "Optimize a local farmers market inventory and promotion strategies by analyzing sales trends, waste ratios, and customer behavior patterns.",
      previewCharts: 3,
      icon: "🥬",
      path: "/case7"
    },
    {
      case: "Case 8",
      level: "Intermediate",
      topic: "MetroMove – City Transit Expansion Plan",
      description: "Analyze public transportation data to recommend zone prioritization and transit mode funding based on performance, coverage, and satisfaction metrics.",
      previewCharts: 3,
      icon: "🚇",
      path: "/case8"
    },
    {
      case: "Case 9",
      level: "Intermediate",
      topic: "AquaLine – Urban Water Consumption Challenge",
      description: "Evaluate the effectiveness of a water conservation program across urban zones, analyzing usage patterns, demographic responses, and infrastructure needs.",
      previewCharts: 3,
      icon: "💧",
      path: "/case9"
    },
    {
      case: "Case 11",
      level: "Advanced",
      topic: "TerraTech – Sustainable Mining Allocation",
      description: "Analyze mining site performance to optimize resource allocation across sustainability programs, balancing extraction output with environmental impact reduction.",
      previewCharts: 3,
      icon: "⛏️",
      path: "/case11"
    },
    {
      case: "Case 12",
      level: "Intermediate",
      topic: "SolarShare Split",
      description: "Optimize solar panel distribution across city neighborhoods based on population, rooftop space, and energy consumption patterns to maximize fossil fuel reduction.",
      previewCharts: 3,
      icon: "☀️",
      path: "/case12"
    },
    {
      case: "Case 13",
      level: "Intermediate",
      topic: "NutriFarm Forecast",
      description: "Analyze farm performance metrics to optimize subsidy allocation for health-focused crops, balancing nutrition content, yield rates, and market value.",
      previewCharts: 3,
      icon: "🌱",
      path: "/case13"
    },
    {
      case: "Case 14",
      level: "Advanced",
      topic: "DeepCurrent Minerals",
      description: "Evaluate underwater mining sites to select optimal locations based on mineral purity, profitability, and environmental impact, balancing revenue with sustainability.",
      previewCharts: 3,
      icon: "🌊",
      path: "/case14"
    },
    {
      case: "Case 15",
      level: "Beginner",
      topic: "SkySprout Planters",
      description: "Analyze urban farming trial data to determine optimal conditions for plant survival, evaluating the impact of sunlight and watering frequency across different neighborhoods.",
      previewCharts: 3,
      icon: "🪴",
      path: "/case15"
    },
    {
      case: "Case 16",
      level: "Beginner",
      topic: "TreeCover Survey",
      description: "Analyze urban green space data to evaluate the relationship between tree cover, public access, and community satisfaction across city blocks, identifying areas for improvement and successful models.",
      previewCharts: 3,
      icon: "🌳",
      path: "/case16"
    },
    {
      case: "Case 17",
      level: "Advanced",
      topic: "QuantumFlow – Quantum Computing Startup",
      description: "Analyze quantum platform performance across multiple dimensions including coherence time, gate fidelity, error rates, and algorithm success probabilities to determine optimal investment strategy.",
      previewCharts: 4,
      icon: "⚛️",
      path: "/case17"
    }
  ];

  const features = [
    { icon: '📈', title: 'Read charts faster', text: 'Get comfortable with exhibits and multi-axis charts so you can spot trends and comparisons quickly.' },
    { icon: '🧮', title: 'Get cleaner with percentages, ratios, and quick calculations', text: 'Sharpen business math and avoid common calculation pitfalls under time pressure.' },
    { icon: '🎯', title: 'Practise finding the important signal in messy data', text: 'Zero in on what matters and turn raw data into clear insights.' },
    { icon: '✍️', title: 'Turn analysis into sharper recommendations under time pressure', text: 'Build the habit of concise, evidence-based conclusions in the report phase.' }
  ];

  const scrollToExplore = () => {
    const exploreSection = document.getElementById('explore');
    exploreSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderCaseCard = (caseItem, isAdditional = false) => {
    const caseNumber = caseItem.case.split(' ')[1];
    const featureKey = getCaseFeatureKey(caseNumber);
    const hasAccess = canAccess(featureKey);

    return (
      <motion.div
        key={caseItem.case}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: isAdditional ? 0.1 : 0 }}
        className={`case-card rounded-xl p-5 md:p-7 shadow-md text-left ${
          caseItem.isUpgradeCard ? 'bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 relative overflow-hidden' : ''
        } ${!hasAccess && !caseItem.isUpgradeCard ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={() => !caseItem.isUpgradeCard && handleCaseClick(caseItem)}
        style={{ position: 'relative' }}
      >
        {!caseItem.isUpgradeCard ? (
          <>
            <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
              <span className="text-xl md:text-2xl bg-blue-50 w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center shadow-inner">{caseItem.icon}</span>
              <div className="flex-1">
                <h3 className="text-blue-600 font-semibold text-sm md:text-base">{caseItem.case} · {caseItem.level}</h3>
                <div className="h-1 w-10 md:w-12 bg-gradient-to-r from-blue-200 to-blue-100 rounded mt-1 md:mt-2"></div>
              </div>
              {caseItem.isFree && (
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-gradient-to-r from-green-400 to-green-500 text-white text-xs font-bold px-2 md:px-3 py-1 md:py-1.5 rounded-full shadow-lg transform transition-transform duration-200 flex items-center gap-1 md:gap-1.5"
                >
                  <span className="text-xs md:text-sm">🎁</span>
                  <span className="hidden sm:inline">{caseItem.case === 'Case 1' ? 'SAMPLE CASE' : 'FREE WITH ACCOUNT'}</span>
                  <span className="sm:hidden">{caseItem.case === 'Case 1' ? 'SAMPLE' : 'FREE'}</span>
                </motion.div>
              )}
              {!hasAccess && !caseItem.isFree && (
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="bg-gradient-to-r from-blue-400 to-blue-500 text-white text-xs font-bold px-2 md:px-3 py-1 md:py-1.5 rounded-full shadow-lg transform transition-transform duration-200 flex items-center gap-1 md:gap-1.5"
                >
                  <span className="text-xs md:text-sm">🔒</span>
                  <span className="hidden sm:inline">FREE WITH ACCOUNT</span>
                  <span className="sm:hidden">🔒</span>
                </motion.div>
              )}
            </div>
            <p className="text-gray-800 font-medium mb-4 md:mb-6 min-h-[3rem] text-sm md:text-base">{caseItem.topic}</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-auto">
              {caseItem.case === 'Case 1' ? (
                <>
                  {caseItem.path && (
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigation(caseItem.path, caseNumber);
                      }}
                      className="preview-button text-xs sm:text-sm text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1 sm:gap-2 transition-colors px-3 sm:px-4 py-2 rounded-lg group w-full justify-center"
                      style={{ position: 'relative' }}
                    >
                      <span className="transform group-hover:scale-110 transition-transform">▶️</span>
                      <span>Start Case</span>
                    </motion.button>
                  )}
                </>
              ) : (
                <FeatureAccessGate feature={featureKey} compact={!isAdditional} fallback={
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/auth');
                    }}
                    className="preview-button text-xs sm:text-sm text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1 sm:gap-2 transition-colors px-3 sm:px-4 py-2 rounded-lg group w-full justify-center"
                  >
                    <span className="transform group-hover:scale-110 transition-transform">🔓</span>
                    <span>Sign in to access</span>
                  </motion.button>
                }>
                  {caseItem.path && (
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigation(caseItem.path, caseNumber);
                      }}
                      className="preview-button text-xs sm:text-sm text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1 sm:gap-2 transition-colors px-3 sm:px-4 py-2 rounded-lg group w-full justify-center"
                    >
                      <span className="transform group-hover:scale-110 transition-transform">▶️</span>
                      <span>Start Case</span>
                    </motion.button>
                  )}
                </FeatureAccessGate>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="absolute top-0 right-0 w-20 h-20 sm:w-28 sm:h-28 bg-blue-100 rounded-bl-full transform translate-x-10 -translate-y-10 sm:translate-x-14 sm:-translate-y-14"></div>
            <div className="absolute bottom-0 left-0 w-20 h-20 sm:w-28 sm:h-28 bg-blue-100 rounded-tr-full transform -translate-x-10 translate-y-10 sm:-translate-x-14 sm:translate-y-14"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
                <span className="text-xl md:text-2xl bg-blue-100 w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center shadow-inner">{caseItem.icon}</span>
                <div className="flex-1">
                  <h3 className="text-blue-600 font-semibold text-sm md:text-base">{caseItem.case}</h3>
                  <div className="h-1 w-10 md:w-12 bg-gradient-to-r from-blue-200 to-blue-100 rounded mt-1 md:mt-2"></div>
                </div>
                <div className="relative">
                  <div className="absolute -top-6 md:-top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <div className="flex items-center gap-1 md:gap-2">
                      <div className="h-0.5 w-4 md:w-8 bg-gradient-to-r from-transparent to-blue-500"></div>
                      <span className="text-blue-500 font-medium tracking-wider animate-pulse text-xs md:text-sm">PRESS ME</span>
                      <div className="h-0.5 w-4 md:w-8 bg-gradient-to-l from-transparent to-blue-500"></div>
                    </div>
                  </div>
                  <motion.button 
                    whileHover={{ scale: 1.1, rotate: 180 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowAdditionalCases(!showAdditionalCases)}
                    className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-white shadow-lg transform transition-transform duration-200 group"
                  >
                    <span className="text-lg md:text-xl transform group-hover:rotate-12 transition-transform duration-200">🔌</span>
                  </motion.button>
                </div>
              </div>
              <div className="flex flex-col gap-2 md:gap-3 mt-3 md:mt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e) => { e.stopPropagation(); navigate('/auth'); }}
                  className="preview-button text-xs sm:text-sm text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1 md:gap-2 transition-colors px-3 md:px-4 py-2 rounded-lg group w-full justify-center"
                >
                  <span className="transform group-hover:scale-110 transition-transform">🔓</span>
                  <span>Sign in to see all cases</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={(e) => { e.stopPropagation(); navigate('/redrock'); }}
                  className="preview-button text-xs sm:text-sm text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1 md:gap-2 transition-colors px-3 md:px-4 py-2 rounded-lg group w-full justify-center"
                >
                  <span className="transform group-hover:scale-110 transition-transform">▶️</span>
                  <span>Try the Real Simulator</span>
                </motion.button>
              </div>
            </div>
          </>
        )}
      </motion.div>
    );
  };

  return (
    <>
      <SEO
        title="Free Red Rock Practice Cases & Guide | Cognidoor"
        description="Free Red Rock practice: math drills, chart reading, logic, and recommendations in one place. 17 timed cases—no payment required. Then step into the real Red Rock simulation with scoring and feedback."
        url="/redrock-guide"
        keywords={['red rock practice', 'free red rock cases', 'mckinsey solve', 'red rock guide', 'consulting case prep']}
      />
      <div className="bg-white text-gray-900 font-sans">
        {/* Hero Section - Enhanced gradient */}
        <section className="relative z-30 bg-gradient-to-br from-blue-100 via-blue-50 to-white pb-8 md:pb-12 min-h-[450px] md:min-h-[520px] flex items-center overflow-visible">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-700/80">
            <div className="absolute inset-0" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'overlay'
            }} />
          </div>
          <div className="container px-4 sm:px-6 md:pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 py-10 md:py-16 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl hero-content"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="inline-block bg-gradient-to-r from-blue-600 to-blue-400 text-white px-4 sm:px-6 py-2 rounded-full shadow-lg mb-4 coming-soon-banner"
              >
                <span className="font-bold text-base md:text-lg tracking-wide coming-soon-text">Free Practice Library</span>
              </motion.div>
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight text-white">
                Practise Red Rock Skills for Free
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl text-white/90 mb-4 md:mb-6 leading-relaxed">
                Math drills, chart reading, logic, and recommendation practice in one place—all free. Work through 17 timed Red Rock-style cases to build the skills; no payment required. When you're ready, step into the real Red Rock simulation with scoring and feedback.
              </p>
              <p className="text-white/80 text-sm md:text-base mb-6 md:mb-8">
                17 timed cases · Math, charts & logic in one go · Beginner to advanced · Free account unlocks full library
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-6 sm:mt-8">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scrollToExplore}
                  className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300 w-full sm:w-auto text-center"
                >
                  Start Free Practice
                  <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/redrock')}
                  className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-gray-700 to-gray-600 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300 w-full sm:w-auto text-center"
                >
                  Try the Red Rock Simulation
                  <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/consulting-math-guides')}
                  className="group relative px-6 sm:px-8 py-3 sm:py-4 border-2 border-blue-600 text-blue-600 text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:bg-blue-50 w-full sm:w-auto text-center"
                >
                  Math & chart prep
                  <div className="absolute inset-0 bg-blue-600 rounded-xl opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                </motion.button>
              </div>
              <p className="mt-4 text-white/80 text-sm">
                <button type="button" onClick={() => document.getElementById('preparation-guide')?.scrollIntoView({ behavior: 'smooth' })} className="underline hover:text-white transition-colors">View Red Rock Guide</button>
              </p>
            </motion.div>
          </div>
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="absolute top-[100px] right-10 md:right-20 w-[250px] sm:w-[320px] md:w-[380px] aspect-[2/3] z-10 hidden md:block hero-image"
          >
            <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
              <img 
                src={redRockImage} 
                alt="Red Rock Case Studies" 
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
              />
            </div>
          </motion.div>
        </section>

        {/* What You'll Get */}
        <section className="py-12 md:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-4 sm:px-6 relative">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-8 md:mb-12 section-title tracking-tight">What You'll Get</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto">
            {features.map(({ icon, title, text }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="feature-card rounded-2xl shadow-md p-6 md:p-8 backdrop-blur-sm"
              >
                <div className="feature-icon text-2xl mb-4 md:mb-6">{icon}</div>
                <h3 className="text-lg font-semibold mb-2 md:mb-3 text-gray-800">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 md:mt-12 text-center">
            <motion.button 
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/redrock')}
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base sm:text-lg font-semibold rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
            >
              Try the Red Rock Simulation
              <span className="opacity-90">→</span>
            </motion.button>
          </div>
        </section>

        {/* Red Rock Case Explorer */}
        <section id="explore" className="py-12 md:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-4 sm:px-6 text-center relative">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 md:mb-6 section-title tracking-tight">Explore the Red Rock Practice Library</h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto text-base md:text-lg">
            These cases are built to help you practise the skills Red Rock tests. Each one is timed and structured so you can train how you interpret exhibits, work through calculations, and turn findings into a recommendation. They are best used as preparation before the full simulator.
          </p>
          <p className="text-gray-600 mb-8 md:mb-12 max-w-2xl mx-auto text-base">
            Ready for the real thing? <button type="button" onClick={() => navigate('/redrock')} className="text-blue-600 font-semibold hover:underline focus:outline-none">Try the Red Rock Simulation</button> for timed runs, scoring, and feedback.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-6xl mx-auto">
            {cases.map((caseItem) => renderCaseCard(caseItem))}
          </div>

          {/* Additional Cases Carousel */}
          {showAdditionalCases && (
            <div className="mt-8 md:mt-12 max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-gray-800">All Cases</h3>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAdditionalCases(false)}
                  className="text-blue-600 hover:text-blue-700 flex items-center gap-2 text-sm md:text-base"
                >
                  <span>Collapse</span>
                  <span>▲</span>
                </motion.button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {additionalCases.map((caseItem) => renderCaseCard(caseItem, true))}
              </div>
            </div>
          )}
        </section>

        {/* Red Rock simulation proof section */}
        <RedRockProofBlock navigate={navigate} />

        {/* Red Rock Preparation Guide Section */}
        <section id="preparation-guide" className="py-12 md:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-4 sm:px-6 relative">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold section-title tracking-tight">Red Rock Preparation Guide</h2>
            </div>

            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {/* Strategy Cards */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="feature-card rounded-2xl p-6 md:p-8"
                >
                  <div className="feature-icon text-2xl md:text-3xl mb-4 md:mb-6">🧠</div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-800">Cognitive Strategy</h3>
                  <ul className="space-y-2 md:space-y-3 text-gray-600 text-sm md:text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Avoid calculator overuse, use scratchpad instead</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Focus on pattern recognition and critical thinking</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Master strategic decision-making under pressure</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="feature-card rounded-2xl p-6 md:p-8"
                >
                  <div className="feature-icon text-2xl md:text-3xl mb-4 md:mb-6">📊</div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-800">Graph Interpretation</h3>
                  <ul className="space-y-2 md:space-y-3 text-gray-600 text-sm md:text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Choose based on comparison type: trend vs category vs composition</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Master line, bar, pie, and stacked charts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Understand hierarchical data visualization</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="feature-card rounded-2xl p-6 md:p-8"
                >
                  <div className="feature-icon text-2xl md:text-3xl mb-4 md:mb-6">⏱</div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-800">Time Management</h3>
                  <ul className="space-y-2 md:space-y-3 text-gray-600 text-sm md:text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Spend 10–12 mins max on Investigation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Leave 5–8 mins for Report phase</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Practice efficient problem-solving techniques</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="feature-card rounded-2xl p-6 md:p-8"
                >
                  <div className="feature-icon text-2xl md:text-3xl mb-4 md:mb-6">✍️</div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-800">Report Phase</h3>
                  <ul className="space-y-2 md:space-y-3 text-gray-600 text-sm md:text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Focus on clarity and relevance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Avoid vague summaries</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Include cause-effect chains with data points</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="feature-card rounded-2xl p-6 md:p-8"
                >
                  <div className="feature-icon text-2xl md:text-3xl mb-4 md:mb-6">💡</div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-800">Formula Tips</h3>
                  <ul className="space-y-2 md:space-y-3 text-gray-600 text-sm md:text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Use the right % change formula for context</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Master weighted averages and probability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Understand expected value calculations</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="feature-card rounded-2xl p-6 md:p-8"
                >
                  <div className="feature-icon text-2xl md:text-3xl mb-4 md:mb-6">🧾</div>
                  <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4 text-gray-800">Journal Efficiency</h3>
                  <ul className="space-y-2 md:space-y-3 text-gray-600 text-sm md:text-base">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Use the journal effectively</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Capture all relevant information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Organize findings systematically</span>
                    </li>
                  </ul>
                </motion.div>
              </div>

              <div className="mt-8 md:mt-12 text-center">
                <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
                  Master Essential Skills for Red Rock Success
                </h3>
                <p className="text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto text-sm md:text-base">
                  Learn advanced math techniques, chart interpretation strategies, and time-saving tricks to excel in your Red Rock cases. Our comprehensive guide will help you develop the analytical mindset needed to tackle any challenge.
                </p>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/consulting-math-guides')}
                  className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base md:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <span className="flex items-center gap-2">
                    <span>Start Your Red Rock Preparation</span>
                    <span className="transform group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Video Modal */}
        <AnimatePresence>
          {showVideoModal && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4"
              onClick={() => setShowVideoModal(false)}
            >
              <motion.div 
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                className="bg-white rounded-2xl p-4 max-w-4xl w-full shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                {/* Video Tabs */}
                <div className="flex space-x-2 mb-4 border-b border-gray-200">
                  {videoTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveVideoTab(tab.id)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                        activeVideoTab === tab.id
                          ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      {tab.title}
                    </button>
                  ))}
                </div>

                {/* Video Container */}
                <div className="relative pb-[56.25%] h-0 rounded-lg overflow-hidden bg-gray-100">
                  {videoTabs.map((tab) => (
                    <div
                      key={tab.id}
                      className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300 ${
                        activeVideoTab === tab.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      }`}
                    >
                      <iframe
                        className="absolute top-0 left-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${tab.videoId}?start=${tab.startTime}`}
                        title={tab.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex justify-end">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowVideoModal(false)}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal Preview */}
        <AnimatePresence>
          {selectedCase && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4"
            >
              <motion.div 
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                className="bg-white rounded-2xl p-4 sm:p-6 max-w-xl w-full shadow-xl overflow-y-auto max-h-[90vh] scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-gray-100"
              >
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">{selectedCase.icon}</span>
                  <h3 className="text-base sm:text-xl font-bold text-blue-600">{selectedCase.case}: {selectedCase.topic}</h3>
                </div>
                <p className="text-gray-700 text-xs sm:text-sm mb-4 sm:mb-6">{selectedCase.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  {[...Array(selectedCase.previewCharts)].map((_, i) => (
                    <div key={i} className="bg-gray-100 rounded-lg aspect-video flex items-center justify-center text-gray-400 text-xs">
                      Chart Preview {i + 1}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-4 sm:mt-6">
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCase(null)}
                    className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Close Preview
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      if (selectedCase.path) {
                        handleNavigation(selectedCase.path, selectedCase.case.split(' ')[1]);
                      }
                    }}
                    className={`bg-gradient-to-r from-blue-600 to-blue-400 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg hover:shadow-lg transition-all duration-300 text-xs sm:text-sm ${!selectedCase.path ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Start This Case
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom CTA */}
        <section className="py-10 md:py-16 text-center px-4 sm:px-6 bg-gradient-to-t from-blue-50 to-white">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-semibold mb-2 md:mb-3">Ready for the real Red Rock experience?</h2>
            <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">The practice library helps you build the skills. The simulator helps you rehearse the real assessment feel. Start with free practice, then step into the near-exact Red Rock simulation when you're ready.</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/redrock')}
                className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 text-sm md:text-base"
              >
                Try the Real Simulator
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/auth')}
                className="border-2 border-blue-600 text-blue-600 px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 text-sm md:text-base"
              >
                Sign in for full library
              </motion.button>
            </div>
          </div>
        </section>

        {/* Red Rock FAQ */}
        <section className="py-10 md:py-14 px-4 sm:px-6 bg-white border-t border-gray-100">
          <div className="max-w-2xl mx-auto text-left">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 section-title tracking-tight">Red Rock practice: quick answers</h2>
            <div className="space-y-3">
              {[
                { q: 'What is Red Rock?', a: 'Red Rock is a McKinsey Solve-style case assessment. You work through a case brief, gather evidence, answer analysis questions, fill in a report, and complete chart and mini-case tasks under time pressure.' },
                { q: 'Are these 17 cases the real Red Rock simulation?', a: 'No. These 17 cases are practice drills built to help you build the same skills—chart reading, business math, logic, and recommendations. The real Red Rock experience is the full simulator (timed run, report phase, graph selection), which you can access separately.' },
                { q: 'What skills do these practice cases help me build?', a: 'Chart and exhibit interpretation, percentages and ratios, quick business math, finding the important signal in data, and turning analysis into clear recommendations under time pressure.' },
                { q: 'Do I need an account to access the practice library?', a: 'Case 1 is available to everyone. For cases 2–17, you need a free account. Sign up once—no payment required—to unlock the full practice library.' },
                { q: "What's the difference between the practice library and the simulator?", a: 'The practice library is 17 timed cases you can use to build skills. The simulator is a near-exact Red Rock experience: full run, report phase, graph selection, and scoring. Use the library to practise; use the simulator to rehearse the real assessment.' }
              ].map((faq, i) => (
                <details key={i} className="group rounded-xl border border-gray-200 bg-gray-50/50 hover:bg-blue-50/30 transition-colors">
                  <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-800 list-none flex items-center justify-between gap-2">
                    <span>{faq.q}</span>
                    <span className="text-blue-500 group-open:rotate-180 transition-transform shrink-0">▼</span>
                  </summary>
                  <p className="px-4 pb-4 pt-1 text-gray-600 text-sm md:text-base leading-relaxed border-t border-gray-100">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
} 
